/**
 * Transaction process graph for bookings:
 *   - default-booking
 *
 * Dear Danielle runs a fulfilment/return flow on top of the stock booking
 * process: after acceptance the provider marks the handoff, the customer
 * (optionally) confirms receipt and later marks the return, and the provider
 * confirms the item is back — which is the payout moment. Every manual step
 * has a fallback timer in the process, so a transaction can never stall in a
 * state waiting for a button press.
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // Customer can cancel booking before provider accepts
  CUSTOMER_CANCEL: 'transition/customer-cancel',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition. One named transition per state the
  // refund lever is available from.
  CANCEL: 'transition/cancel',
  CANCEL_FROM_SHIPPED: 'transition/cancel-from-shipped',
  CANCEL_FROM_IN_USE: 'transition/cancel-from-in-use',
  CANCEL_FROM_RETURNED: 'transition/cancel-from-returned',

  // Fulfilment: the provider marks the handoff (ship or in person).
  MARK_SHIPPED: 'transition/mark-shipped',
  // The customer confirms receipt — optional; a timer advances the state at
  // booking end regardless.
  MARK_RECEIVED: 'transition/mark-received',
  AUTO_MARK_IN_USE: 'transition/auto-mark-in-use',
  // The customer's outs when the item never arrives or arrives with a problem.
  REPORT_NOT_DELIVERED: 'transition/report-not-delivered',
  REPORT_PROBLEM: 'transition/report-problem',

  // Return: the customer sends the item back, the provider confirms it is
  // back in hand (the payout moment) — or disputes.
  MARK_RETURNED: 'transition/mark-returned',
  MARK_RETURN_RECEIVED: 'transition/mark-return-received',
  AUTO_CONFIRM_RETURN: 'transition/auto-confirm-return',
  DISPUTE: 'transition/dispute',
  DISPUTE_RETURNED: 'transition/dispute-returned',

  // Operator resolves a dispute: full payout or full refund.
  RESOLVE_DISPUTE_COMPLETE: 'transition/resolve-dispute-complete',
  RESOLVE_DISPUTE_REFUND: 'transition/resolve-dispute-refund',

  // The backend automatically completes transactions whose participants went
  // quiet: from accepted (never marked shipped) 2 days after booking end, and
  // from in-use (never marked returned) 7 days after booking end.
  COMPLETE: 'transition/complete',
  AUTO_COMPLETE: 'transition/auto-complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 *       Exception: states listed in statesNeedingProviderAttention are used in
 *       API queries and must match the process definition's state names.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  SHIPPED: 'shipped',
  IN_USE: 'in-use',
  RETURNED: 'returned',
  DISPUTED: 'disputed',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.CUSTOMER_CANCEL]: states.CANCELED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.MARK_SHIPPED]: states.SHIPPED,
        [transitions.REPORT_NOT_DELIVERED]: states.DISPUTED,
        [transitions.COMPLETE]: states.COMPLETED,
        [transitions.OPERATOR_COMPLETE]: states.COMPLETED,
      },
    },

    [states.SHIPPED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.IN_USE,
        [transitions.AUTO_MARK_IN_USE]: states.IN_USE,
        [transitions.REPORT_PROBLEM]: states.DISPUTED,
        [transitions.CANCEL_FROM_SHIPPED]: states.CANCELED,
      },
    },

    [states.IN_USE]: {
      on: {
        [transitions.MARK_RETURNED]: states.RETURNED,
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.CANCEL_FROM_IN_USE]: states.CANCELED,
      },
    },

    [states.RETURNED]: {
      on: {
        [transitions.MARK_RETURN_RECEIVED]: states.COMPLETED,
        [transitions.AUTO_CONFIRM_RETURN]: states.COMPLETED,
        [transitions.DISPUTE_RETURNED]: states.DISPUTED,
        [transitions.CANCEL_FROM_RETURNED]: states.CANCELED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.RESOLVE_DISPUTE_COMPLETE]: states.COMPLETED,
        [transitions.RESOLVE_DISPUTE_REFUND]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},
    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CUSTOMER_CANCEL,
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.CANCEL_FROM_SHIPPED,
    transitions.CANCEL_FROM_IN_USE,
    transitions.CANCEL_FROM_RETURNED,
    transitions.MARK_SHIPPED,
    transitions.MARK_RECEIVED,
    transitions.MARK_RETURNED,
    transitions.MARK_RETURN_RECEIVED,
    transitions.AUTO_CONFIRM_RETURN,
    transitions.REPORT_NOT_DELIVERED,
    transitions.REPORT_PROBLEM,
    transitions.DISPUTE,
    transitions.DISPUTE_RETURNED,
    transitions.RESOLVE_DISPUTE_COMPLETE,
    transitions.RESOLVE_DISPUTE_REFUND,
    transitions.COMPLETE,
    transitions.AUTO_COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (booking over, payout made)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.AUTO_COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.MARK_RETURN_RECEIVED,
    transitions.AUTO_CONFIRM_RETURN,
    transitions.RESOLVE_DISPUTE_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.CUSTOMER_CANCEL,
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CANCEL_FROM_SHIPPED,
    transitions.CANCEL_FROM_IN_USE,
    transitions.CANCEL_FROM_RETURNED,
    transitions.RESOLVE_DISPUTE_REFUND,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

// These state names are used in API queries (state/<name>), so they must match
// the process definition exactly: a request awaiting acceptance, a handoff
// waiting to be marked shipped, and a return waiting to be confirmed.
export const statesNeedingProviderAttention = [
  states.PREAUTHORIZED,
  states.ACCEPTED,
  states.RETURNED,
];
